import React from 'react';

import {

  Box, Card, Chip, Stack, Typography,

} from '@mui/material';

import { alpha, useTheme } from '@mui/material/styles';

import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';

import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';

import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';

import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';



import { EMPTY_VALUE, formatTonne } from '../../../shared/utils/number';

import { MONO_FONT, TOKENS } from '../../../theme/muiTheme';

import { CLIMATE_ACTION_COLORS } from '../model/climateActionColors';



const CARBON_LEDGER_MONO = `'IBM Plex Mono', ${MONO_FONT}`;



export const CLIMATE_ACTION_KPI_COLORS = Object.freeze({
  gross: CLIMATE_ACTION_COLORS.inventory,
  target: CLIMATE_ACTION_COLORS.target,
  varianceAhead: TOKENS.success,
  varianceBehind: TOKENS.error,
  varianceUnavailable: TOKENS.textSecondary,
  impact: '#3b82f6',
});



const METRICS = [

  { id: 'gross', icon: CloudOutlinedIcon, color: CLIMATE_ACTION_KPI_COLORS.gross },

  { id: 'target', icon: FlagOutlinedIcon, color: CLIMATE_ACTION_KPI_COLORS.target },

  { id: 'gap', icon: TimelineOutlinedIcon, color: CLIMATE_ACTION_KPI_COLORS.varianceUnavailable },

  { id: 'impact', icon: FactCheckOutlinedIcon, color: CLIMATE_ACTION_KPI_COLORS.impact },

];



const VARIANCE_CAPTION_COPY = Object.freeze({

  ON_BASELINE: {

    label: 'climateAction.kpi.onBaselineVariance',

    secondary: 'climateAction.kpi.onBaselineHint',

  },

  BEHIND_TARGET: {

    label: 'climateAction.kpi.aboveVariance',

    secondary: 'climateAction.kpi.behindTarget',

  },

  AHEAD_OF_TARGET: {

    label: 'climateAction.kpi.aheadVariance',

    secondary: 'climateAction.kpi.aheadTarget',

  },

  ON_TARGET: {

    label: 'climateAction.kpi.onTargetVariance',

    secondary: 'climateAction.kpi.onTargetHint',

  },

  UNAVAILABLE: {

    label: 'climateAction.kpi.gap',

    secondary: 'climateAction.kpi.gap',

  },

});



const metricTone = (metric, theme) => {

  if (theme.palette.mode !== 'dark') return metric.color;

  if (metric.id === 'gross') return CLIMATE_ACTION_COLORS.inventoryDark;

  if (metric.id === 'target') return CLIMATE_ACTION_COLORS.targetDark;

  if (metric.id === 'impact') return '#60a5fa';

  if (metric.color === CLIMATE_ACTION_KPI_COLORS.varianceBehind) return '#f87171';

  if (metric.color === CLIMATE_ACTION_KPI_COLORS.varianceAhead) return '#65D9A5';

  return theme.palette.text.secondary;

};



export const varianceKpiColor = ({ available, gap }) => {

  if (!available) return CLIMATE_ACTION_KPI_COLORS.varianceUnavailable;

  return Number(gap) > 0

    ? CLIMATE_ACTION_KPI_COLORS.varianceBehind

    : CLIMATE_ACTION_KPI_COLORS.varianceAhead;

};



const numericValue = (...values) => {

  const value = values.find(candidate => (

    candidate !== null && candidate !== undefined && Number.isFinite(Number(candidate))

  ));

  return value === undefined ? null : Number(value);

};



/** Keep management impact totals separate from Carbon Credit Balance accounting. */

export const impactKpiPresentation = (impactSummary = {}, otherImpact = {}) => {

  const verifiedInBoundaryReductionsTco2e = numericValue(

    impactSummary.verifiedInBoundaryReductionsTco2e,

    impactSummary.verifiedInBoundaryTco2e,

    impactSummary.verifiedTco2e,
    impactSummary.verified_tco2e,

  ) ?? 0;

  const unverifiedInBoundaryReductionsTco2e = numericValue(

    impactSummary.unverifiedInBoundaryReductionsTco2e,

    impactSummary.unverifiedTco2e,
    impactSummary.unverified_tco2e,

  ) ?? 0;

  const otherVerifiedClimateActionImpactTco2e = numericValue(

    otherImpact.verifiedTco2e,
    otherImpact.verified_tco2e,

  ) ?? 0;

  return {

    otherVerifiedClimateActionImpactTco2e,

    totalVerifiedActionResultsTco2e: verifiedInBoundaryReductionsTco2e

      + otherVerifiedClimateActionImpactTco2e,

    unverifiedInBoundaryReductionsTco2e,

    verifiedInBoundaryReductionsTco2e,

  };

};



function MetricShell({ children, label, metric }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const Icon = metric.icon;
  const tone = metricTone(metric, theme);

  return (
    <Card component={'article'} sx={{
      backgroundColor: isDark
        ? `linear-gradient(155deg, ${alpha(tone, 0.16)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 58%)`
        : `linear-gradient(155deg, ${alpha(tone, 0.09)} 0%, #FFFFFF 62%)`,
      borderColor: alpha(tone, isDark ? 0.32 : 0.24),
      borderRadius: 1.75,
      borderTop: `4px solid ${tone}`,
      boxShadow: isDark
        ? `0 10px 28px ${alpha(tone, 0.2)}`
        : `0 10px 28px ${alpha(tone, 0.16)}`,
      height: '100%',
      minHeight: 164,
      overflow: 'hidden',
      p: 1.75,
    }}>
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', height: '100%', rowGap: 1.35 }}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} spacing={1.5}>
          <Typography component={'h2'} sx={{ color: 'text.primary', fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>{label}</Typography>
          <Box sx={{
            alignItems: 'center', bgcolor: alpha(tone, isDark ? 0.24 : 0.14),
            border: `1px solid ${alpha(tone, isDark ? 0.3 : 0.2)}`,
            borderRadius: 1.5, color: tone, display: 'flex', height: 32,
            justifyContent: 'center', width: 32,
          }}>
            <Icon sx={{ fontSize: 22 }} />
          </Box>
        </Stack>

        {children}
      </Box>
    </Card>
  );
}

function StandardMetric({ label, metric, secondary, value, signed = false, badge = null }) {

  const theme = useTheme();

  return (

    <MetricShell label={label} metric={metric}>

      <Stack justifyContent={'space-between'} spacing={1.5}>

        <Stack alignItems={'baseline'} direction={'row'} flexWrap={'wrap'} spacing={1}>

          <Typography sx={{

            color: metricTone(metric, theme),

            fontFamily: CARBON_LEDGER_MONO,

            fontSize: 28,

            fontWeight: 700,

            letterSpacing: '-0.035em',

            whiteSpace: 'nowrap',

          }}>

            {value === null || value === undefined
              ? EMPTY_VALUE
              : `${signed && Number(value) > 0 ? '+' : ''}${formatTonne(value, { withUnit: true })}`}

          </Typography>

          {badge}

        </Stack>

        <Typography color={'text.secondary'} sx={{ fontSize: 14, lineHeight: 1.45 }}>{secondary}</Typography>

      </Stack>

    </MetricShell>

  );

}

function LedgerImpactRow({ color = 'text.primary', label, value }) {

  return (

    <Box sx={{

      alignItems: 'baseline', columnGap: 1, display: 'grid',

      gridTemplateColumns: 'minmax(0, 1fr) minmax(82px, auto)',

    }}>

      <Typography sx={{ color, fontSize: 11.5, fontWeight: 700, lineHeight: 1.3 }}>{label}</Typography>

      <Typography sx={{

        color,

        fontFamily: CARBON_LEDGER_MONO,

        fontSize: 12.5,

        fontWeight: 700,

        textAlign: 'right',

        whiteSpace: 'nowrap',

      }}>

        {formatTonne(value, { withUnit: false })}

      </Typography>

    </Box>

  );

}



/** Show the composed target KPIs for the selected fiscal year. */

export default function ClimateActionKpis({

  impactSummary = {}, otherClimateImpact = {}, position, selectedYear, t,

}) {

  const inventoryIsAvailable = Boolean(position.inventory);

  const targetIsAvailable = position.target !== null;

  const varianceIsAvailable = inventoryIsAvailable && targetIsAvailable && position.gap !== null;

  const varianceCaption = position.varianceCaption || 'UNAVAILABLE';

  const varianceCopy = VARIANCE_CAPTION_COPY[varianceCaption] || VARIANCE_CAPTION_COPY.UNAVAILABLE;

  const impact = impactKpiPresentation(impactSummary, otherClimateImpact);

  const gapMetric = {

    ...METRICS[2],

    color: varianceKpiColor({ available: varianceIsAvailable, gap: position.gap }),

  };

  return (

    <Box sx={{

      display: 'grid',

      gap: 1.5,

      gridTemplateColumns: {

        xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))',

      },

    }}>

      <StandardMetric

        label={t('climateAction.kpi.gross')}

        metric={METRICS[0]}

        secondary={inventoryIsAvailable

          ? t('climateAction.kpi.inventorySource')

          : t('climateAction.kpi.noInventoryForYear', { year: selectedYear })}

        value={inventoryIsAvailable ? position.gross : null}

      />

      <StandardMetric label={t('climateAction.kpi.fyTarget', { year: selectedYear })} metric={METRICS[1]} secondary={position.target === null ? t('climateAction.kpi.noTargetForYear', { year: selectedYear }) : t('climateAction.kpi.targetPathway')} value={position.target} badge={position.isHistoricalTarget ? <Chip color={'warning'} label={t('climateAction.kpi.historicalTarget')} size={'small'} sx={{ height: 20, '& .MuiChip-label': { fontSize: 10.5, fontWeight: 700, px: 1 } }} /> : null} />

      <StandardMetric

        label={t(varianceCopy.label)}

        metric={gapMetric}

        secondary={!inventoryIsAvailable

          ? t('climateAction.kpi.noInventoryForYear', { year: selectedYear })

          : position.target === null

            ? t('climateAction.kpi.noTargetForYear', { year: selectedYear })

            : t(varianceCopy.secondary)}

        signed

        value={varianceIsAvailable ? position.gap : null}

      />

      <MetricShell

        label={t('climateAction.kpi.verifiedActionResults', {

          defaultValue: 'Verified Action Results · FY{{year}}',

          year: selectedYear,

        })}

        metric={METRICS[3]}

      >

        <Stack justifyContent={'space-between'} spacing={1.25}>

          <Typography sx={{

            color: theme => metricTone(METRICS[3], theme),

            fontFamily: CARBON_LEDGER_MONO,

            fontSize: 28,

            fontWeight: 700,

            letterSpacing: '-0.035em',

            whiteSpace: 'nowrap',

          }}>

            {formatTonne(impact.totalVerifiedActionResultsTco2e, { withUnit: true })}

          </Typography>

          <Stack spacing={0.65} sx={{

            bgcolor: 'background.default',

            border: 1,

            borderColor: 'divider',

            borderRadius: 1.5,

            px: 1.15,

            py: 0.8,

          }}>

            <LedgerImpactRow

              color={'success.main'}

              label={t('climateAction.kpi.inBoundaryReduction', {

                defaultValue: 'Verified in-boundary reductions',

              })}

              value={impact.verifiedInBoundaryReductionsTco2e}

            />

            <LedgerImpactRow

              label={t('climateAction.kpi.otherVerifiedClimateAction', {

                defaultValue: 'Other verified climate-action impact',

              })}

              value={impact.otherVerifiedClimateActionImpactTco2e}

            />

            <Typography color='text.secondary' sx={{ fontSize: 10.75, lineHeight: 1.3, mt: 0.15 }}>

              {t('climateAction.impact.trackedSeparately', {

                defaultValue: 'Tracked separately · not deducted from Scope 1–3 emissions',

              })}

            </Typography>

            {Number(impact.unverifiedInBoundaryReductionsTco2e) > 0 && (

              <Typography color='text.secondary' sx={{ fontSize: 10.75, lineHeight: 1.3 }}>

                {t('climateAction.kpi.unverifiedRemaining', {

                  defaultValue: 'Unverified: {{value}} tCO₂e',

                  value: formatTonne(impact.unverifiedInBoundaryReductionsTco2e, { withUnit: false }),

                })}

              </Typography>

            )}

          </Stack>

        </Stack>

      </MetricShell>

    </Box>

  );

}